import { type FormEvent, type ReactElement, useState } from 'react';
import { type ApiError, type CountryPackRetirementView, type CountryPackView } from '@vello/api-client';
import {
  maySelfApprove,
  type CountryPackForm,
  type CountryFormField,
  type CountryFormIssue,
  emptyCountryPackForm,
  normalizeCountryPackForm,
  createCountryDraft,
  listCountryPacks,
  approveCountryPack,
  PACK_RETIRE_REASONS,
  pendingRetirement,
  retireAction,
  proposeCountryPackRetirement,
  approveCountryPackRetirement,
  rejectCountryPackRetirement,
  formatMarketInstant,
  tailOf,
} from '../../src/index';
import { usePersonNames } from '../components/person-name';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { Badge, Button, Field, Notice, PageHeader, Panel, Table, TextInput, type Column, FormRow, SectionTitle, FormGrid, Cluster, Stack, Text } from '@vello/web-theme';
import { useVocab } from '../i18n/vocab';

const STATUS_TONE: Readonly<Record<string, 'neutral' | 'success' | 'warning'>> = {
  DRAFT: 'warning',
  APPROVED: 'success',
  RETIRED: 'neutral',
};

/** issue reason code → localized message id (one issue per field). */
const ISSUE_KEY: Readonly<Record<CountryFormIssue['code'], string>> = {
  INVALID_COUNTRY: 'reg.err.country',
  INVALID_CURRENCY: 'reg.err.currency',
  INVALID_LOCALE: 'reg.err.locale',
  INVALID_SUPPORTED_LOCALE: 'reg.err.supportedLocales',
  INVALID_DATE: 'reg.err.date',
  NOT_AFTER_FROM: 'reg.err.notAfter',
  INVALID_CAPABILITY: 'reg.err.capability',
};

/**
 * Registries — country registration + governance (S113; ADR-0033). The operator registers a country as a
 * versioned, effective-dated `CountryPack` DRAFT (currency, recommended + supported locales, effective window),
 * then a **second** operator approves it (four-eyes, server-enforced). The version history reads back each
 * pack's DRAFT/APPROVED/RETIRED state. Country/currency/locale are data — the form hardcodes no country.
 *
 * D420 (owner decisions D408 #12, D422): an APPROVED version can be **retired** under the same four eyes — one operator
 * proposes (a reason code and a written reason), a DIFFERENT operator approves (the proposer only sees it waiting and
 * may withdraw it). The server refuses while the country still has a market that is not retired.
 */
export function RegistriesScreen(): ReactElement {
  const { runtime, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const { confirm } = useConsole();

  // ---- register-a-country form ----
  const [form, setForm] = useState<CountryPackForm>(() => emptyCountryPackForm());
  const [errors, setErrors] = useState<Partial<Record<CountryFormField, string>>>({});
  const [createMsg, setCreateMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [createBusy, setCreateBusy] = useState(false);

  // ---- country registry (lookup + history) ----
  const [lookup, setLookup] = useState('');
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [packs, setPacks] = useState<readonly CountryPackView[]>([]);
  const [retirements, setRetirements] = useState<readonly CountryPackRetirementView[]>([]);
  const [retireBusy, setRetireBusy] = useState(false);
  const [listMsg, setListMsg] = useState<string | undefined>(undefined);
  const [listBusy, setListBusy] = useState(false);

  // ---- approve a draft version ----
  const [reason, setReason] = useState<Record<number, string>>({});
  const [approveBusy, setApproveBusy] = useState<number | undefined>(undefined);
  const [approveMsg, setApproveMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  const set = (field: keyof CountryPackForm) => (v: string): void => setForm((f) => ({ ...f, [field]: v }));

  const refresh = async (cc: string): Promise<void> => {
    setListBusy(true);
    setListMsg(undefined);
    const res = await listCountryPacks(runtime, cc);
    setListBusy(false);
    if (res.ok) {
      setCountry(res.value.country);
      setPacks(res.value.packs);
      setRetirements(res.value.retirements ?? []);
    } else {
      setListMsg(t('reg.lookup.failed', { code: res.error.code }));
    }
  };

  const onCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setCreateMsg(undefined);
    const normalized = normalizeCountryPackForm(form);
    if (!normalized.ok) {
      const next: Partial<Record<CountryFormField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(ISSUE_KEY[issue.code]);
      setErrors(next);
      return;
    }
    setErrors({});
    setCreateBusy(true);
    const res = await createCountryDraft(runtime, normalized.value);
    setCreateBusy(false);
    if (res.ok) {
      setCreateMsg({ tone: 'success', text: t('reg.created', { country: res.value.pack.countryCode, version: res.value.pack.version }) });
      setLookup(res.value.pack.countryCode);
      await refresh(res.value.pack.countryCode);
    } else {
      setCreateMsg({ tone: 'danger', text: t('reg.create.failed', { code: res.error.code }) });
    }
  };

  const onLookup = async (): Promise<void> => {
    const cc = lookup.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(cc)) {
      setListMsg(t('reg.err.country'));
      return;
    }
    setLookup(cc);
    await refresh(cc);
  };

  const onApprove = async (version: number): Promise<void> => {
    if (country === undefined) return;
    const approvalReason = (reason[version] ?? '').trim();
    setApproveMsg(undefined);
    if (approvalReason === '') {
      setApproveMsg({ tone: 'danger', text: t('reg.approve.needReason') });
      return;
    }
    setApproveBusy(version);
    const res = await approveCountryPack(runtime, country, { version, approvalReason });
    setApproveBusy(undefined);
    if (res.ok) {
      setApproveMsg({ tone: 'success', text: t('reg.approved', { version, settings: res.value.policySettingsPublished }) });
      await refresh(country);
    } else {
      const text = res.error.code === 'FORBIDDEN' ? t('reg.approve.fourEyes') : t('reg.approve.failed', { code: res.error.code });
      setApproveMsg({ tone: 'danger', text });
    }
  };

  // ---- D420: retire an APPROVED version under four eyes ----
  const retireRefusal = (error: ApiError): string => explain(error) ?? t('packret.refused', { code: fail(error) });

  const onProposeRetire = async (version: number): Promise<void> => {
    if (country === undefined) return;
    const labels = PACK_RETIRE_REASONS.map((code) => v('country_pack.retire_reason', code));
    const answer = await confirm({
      title: t('packret.propose.title', { country, version }),
      body: t('packret.propose.body'),
      confirmLabel: t('packret.propose.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: 'danger',
      reasons: labels,
      reasonLabel: t('packret.reasonLabel'),
      notePlaceholder: t('packret.note.placeholder'),
      requireNote: true,
    });
    const reasonCode = answer.reason !== undefined ? PACK_RETIRE_REASONS[labels.indexOf(answer.reason)] : undefined;
    if (!answer.confirmed || reasonCode === undefined || answer.note === undefined) return;
    setApproveMsg(undefined);
    setRetireBusy(true);
    const res = await proposeCountryPackRetirement(runtime, country, { version, reasonCode, reason: answer.note });
    setRetireBusy(false);
    setApproveMsg(res.ok ? { tone: 'success', text: t('packret.proposed', { version }) } : { tone: 'danger', text: retireRefusal(res.error) });
    await refresh(country);
  };

  const onDecideRetire = async (version: number, decision: 'approve' | 'reject'): Promise<void> => {
    if (country === undefined) return;
    const approve = decision === 'approve';
    const answer = await confirm({
      title: approve ? t('packret.approve.title', { country, version }) : t('packret.reject.title', { version }),
      body: approve ? t('packret.approve.body') : t('packret.reject.body'),
      confirmLabel: approve ? t('packret.approve.confirm') : t('packret.reject.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: approve ? 'danger' : 'primary',
      reasonLabel: t('packret.reasonLabel'),
      notePlaceholder: t('packret.note.placeholder'),
      requireNote: true,
    });
    if (!answer.confirmed || answer.note === undefined) return;
    setApproveMsg(undefined);
    setRetireBusy(true);
    const res = approve
      ? await approveCountryPackRetirement(runtime, country, { version, reason: answer.note })
      : await rejectCountryPackRetirement(runtime, country, { version, reason: answer.note });
    setRetireBusy(false);
    setApproveMsg(res.ok ? { tone: 'success', text: t(approve ? 'packret.retired' : 'packret.closed', { version }) } : { tone: 'danger', text: retireRefusal(res.error) });
    await refresh(country);
  };

  const operatorName = usePersonNames(retirements.map((r) => r.proposedBy));
  const retireCell = (p: CountryPackView): ReactElement => {
    const action = retireAction(p, retirements, me?.id, selfApproval);
    const pending = pendingRetirement(retirements, p.version);
    if (action === 'propose') {
      return (
        <Button variant="secondary" disabled={retireBusy} onClick={() => void onProposeRetire(p.version)}>
          {t('packret.retire', { version: p.version })}
        </Button>
      );
    }
    if (pending === undefined) return <Text tone="tertiary">—</Text>;
    return (
      <Stack gap={2} className="vello-ab-minw-lg">
        <Text role="caption" tone="secondary">
          {t('packret.pending', { operator: operatorName(pending.proposedBy) ?? tailOf(pending.proposedBy), reason: `${v('country_pack.retire_reason', pending.reasonCode)} — ${pending.reason}` })}
        </Text>
        {action === 'own_pending' ? (
          <Cluster gap={2}>
            <Badge tone="warning">{t('packret.waiting')}</Badge>
            <Button variant="ghost" aria-label={t('packret.withdraw', { version: p.version })} disabled={retireBusy} onClick={() => void onDecideRetire(p.version, 'reject')}>
              {t('packret.reject.confirm')}
            </Button>
          </Cluster>
        ) : (
          <Cluster gap={2}>
            <Button variant="danger" aria-label={t('packret.approve', { version: p.version })} disabled={retireBusy} onClick={() => void onDecideRetire(p.version, 'approve')}>
              {t('packret.approve.confirm')}
            </Button>
            <Button variant="ghost" aria-label={t('packret.reject', { version: p.version })} disabled={retireBusy} onClick={() => void onDecideRetire(p.version, 'reject')}>
              {t('packret.reject.confirm')}
            </Button>
          </Cluster>
        )}
      </Stack>
    );
  };

  const columns: readonly Column<CountryPackView>[] = [
    { key: 'version', header: t('reg.col.version'), numeric: true, render: (p) => `v${p.version}` },
    { key: 'status', header: t('reg.col.status'), render: (p) => <Badge tone={STATUS_TONE[p.status] ?? 'neutral'}>{v('country_pack.status', p.status)}</Badge> },
    { key: 'currency', header: t('reg.col.currency'), render: (p) => p.currency },
    { key: 'locale', header: t('reg.col.locale'), render: (p) => p.locale },
    // D420: a pack is country-level (a country may span several zones), so its date renders in UTC with the marker.
    { key: 'effectiveFrom', header: t('reg.col.effectiveFrom'), render: (p) => <span className="tnum">{formatMarketInstant(p.effectiveFrom, undefined, lang, 'date')}</span> },
    { key: 'author', header: t('reg.col.author'), render: (p) => p.audit.createdBy },
    { key: 'approver', header: t('reg.col.approver'), render: (p) => p.audit.approvedBy ?? '—' },
    {
      key: 'approve',
      header: t('reg.col.action'),
      render: (p) =>
        p.status === 'DRAFT' ? (
          <Cluster gap={2} className="vello-ab-minw-lg">
            <TextInput
              aria-label={t('reg.approve.reason')}
              value={reason[p.version] ?? ''}
              onChange={(e) => setReason((r) => ({ ...r, [p.version]: e.target.value }))}
              placeholder={t('reg.approve.reason')}
            />
            <Button disabled={approveBusy !== undefined} onClick={() => void onApprove(p.version)}>
              {approveBusy === p.version ? t('reg.approve.busy') : t('reg.approve')}
            </Button>
          </Cluster>
        ) : (
          retireCell(p)
        ),
    },
  ];

  return (
    <>
      <PageHeader title={t('reg.title')} subtitle={t('reg.subtitle')} />

      <Panel>
        <Stack gap={4}>
          <SectionTitle>{t('reg.register')}</SectionTitle>
          <Text as="p" tone="secondary">{t('reg.register.hint')}</Text>
          <form onSubmit={(e) => void onCreate(e)} noValidate>
            <Stack gap={4}>
              <FormGrid>
                <Field label={t('reg.field.country')} htmlFor="reg-country" hint={errors.countryCode ?? t('reg.field.country.hint')}>
                  <TextInput id="reg-country" value={form.countryCode} onChange={(e) => set('countryCode')(e.target.value.toUpperCase())} maxLength={2} placeholder="US" required />
                </Field>
                <Field label={t('reg.field.currency')} htmlFor="reg-currency" hint={errors.currency ?? t('reg.field.currency.hint')}>
                  <TextInput id="reg-currency" value={form.currency} onChange={(e) => set('currency')(e.target.value.toUpperCase())} maxLength={3} placeholder="USD" required />
                </Field>
                <Field label={t('reg.field.locale')} htmlFor="reg-locale" hint={errors.locale ?? t('reg.field.locale.hint')}>
                  <TextInput id="reg-locale" value={form.locale} onChange={(e) => set('locale')(e.target.value)} placeholder="en-US" required />
                </Field>
                <Field label={t('reg.field.supportedLocales')} htmlFor="reg-supported" hint={errors.supportedLocales ?? t('reg.field.supportedLocales.hint')}>
                  <TextInput id="reg-supported" value={form.supportedLocales} onChange={(e) => set('supportedLocales')(e.target.value)} placeholder="en-US, es-419" />
                </Field>
                <Field label={t('reg.field.effectiveFrom')} htmlFor="reg-from" hint={errors.effectiveFrom ?? t('reg.field.date.hint')}>
                  <TextInput id="reg-from" type="date" value={form.effectiveFrom} onChange={(e) => set('effectiveFrom')(e.target.value)} required />
                </Field>
                <Field label={t('reg.field.effectiveTo')} htmlFor="reg-to" hint={errors.effectiveTo ?? t('reg.field.effectiveTo.hint')}>
                  <TextInput id="reg-to" type="date" value={form.effectiveTo} onChange={(e) => set('effectiveTo')(e.target.value)} />
                </Field>
                <Field label={t('reg.field.productCaps')} htmlFor="reg-product" hint={errors.productCapabilities ?? t('reg.field.productCaps.hint')}>
                  <TextInput id="reg-product" value={form.productCapabilities} onChange={(e) => set('productCapabilities')(e.target.value)} placeholder="ride, moto, delivery" />
                </Field>
                <Field label={t('reg.field.paymentCaps')} htmlFor="reg-payment" hint={errors.paymentCapabilities ?? t('reg.field.paymentCaps.hint')}>
                  <TextInput id="reg-payment" value={form.paymentCapabilities} onChange={(e) => set('paymentCapabilities')(e.target.value)} placeholder="instant_payment, card, cash" />
                </Field>
                <Field label={t('reg.field.privacyFramework')} htmlFor="reg-privacy-fw" hint={t('reg.field.privacyFramework.hint')}>
                  <TextInput id="reg-privacy-fw" value={form.privacyFramework} onChange={(e) => set('privacyFramework')(e.target.value)} placeholder="GDPR" />
                </Field>
                <Field label={t('reg.field.privacyRegulator')} htmlFor="reg-privacy-reg" hint={t('reg.field.privacyRegulator.hint')}>
                  <TextInput id="reg-privacy-reg" value={form.privacyRegulator} onChange={(e) => set('privacyRegulator')(e.target.value)} placeholder="CNIL" />
                </Field>
              </FormGrid>
              {createMsg !== undefined && (
                <Notice tone={createMsg.tone}>{createMsg.text}</Notice>
              )}
              <Button type="submit" variant="primary" disabled={createBusy}>
                {createBusy ? t('reg.create.busy') : t('reg.create')}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={4}>
          <SectionTitle>{t('reg.registry')}</SectionTitle>
          <FormRow columns="minmax(0, 220px)"
            action={
              <Button disabled={listBusy} onClick={() => void onLookup()}>
                {listBusy ? t('reg.lookup.busy') : t('reg.lookup')}
              </Button>
            }
          >
            <Field label={t('reg.field.lookup')} htmlFor="reg-lookup" hint={t('reg.field.lookup.hint')}>
              <TextInput id="reg-lookup" value={lookup} onChange={(e) => setLookup(e.target.value.toUpperCase())} maxLength={2} placeholder="US" />
            </Field>
          </FormRow>
          {listMsg !== undefined && <Notice tone="danger">{listMsg}</Notice>}
          {approveMsg !== undefined && (
            <Notice tone={approveMsg.tone}>{approveMsg.text}</Notice>
          )}
          {country !== undefined && (
            <div>
              <Text as="p" role="label" tone="secondary">
                {t('reg.registry.for', { country })} · {t('reg.registry.approverNote', { operator: me?.id ?? '—' })}
              </Text>
              <Table columns={columns} rows={packs} rowKey={(p) => `${p.countryCode}-${p.version}`} empty={<Notice tone="neutral">{t('reg.empty')}</Notice>} />
            </div>
          )}
        </Stack>
      </Panel>
    </>
  );
}
